(() => {
  const namespace = window.LumiNovaPillowCommerceB || {};
  window.LumiNovaPillowCommerceB = namespace;

  const PAGE_VERSION = 'B-v1.0';
  const EVENT_NAME = 'luminova:pillow-commerce';
  const SECTION_PALETTES = Object.freeze({
    'onyx black': ['#ddd8d1', '#aaa198', 'rgba(255,255,255,.58)'],
    merlot: ['#e8b8c2', '#c47589', 'rgba(111,38,57,.18)'],
    'champagne cream': ['#f2e7cf', '#d7bf91', 'rgba(255,253,246,.7)'],
    'powder blue': ['#dceaf3', '#98bcd3', 'rgba(248,253,255,.72)'],
    'blush mauve': ['#ead0d6', '#c28f9c', 'rgba(255,246,249,.66)'],
    'mushroom taupe': ['#dfd2c6', '#ad9581', 'rgba(250,244,238,.62)'],
    'slate blue': ['#d3e0e9', '#819fb5', 'rgba(246,251,254,.67)'],
    'soft mint': ['#dcebe2', '#9abda8', 'rgba(248,255,251,.68)']
  });
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const deviceType = () => window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
  const motionBehavior = () => motionPreference.matches ? 'auto' : 'smooth';

  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: {
        event: name,
        landingPageVersion: PAGE_VERSION,
        device: deviceType(),
        viewportWidth: window.innerWidth,
        ...detail
      }
    }));
  };

  class PillowCommerceBController {
    constructor(root) {
      this.root = root;
      this.mode = 'single';
      this.secondaryVariantId = '';
      this.purchaseInitialized = false;
      this.compactImages = new Map();
      this.compactPreviews = new Map();
      this.compactViewport = window.matchMedia('(max-width: 749px)');
      this.abortController = new AbortController();
      this.reachObserver = null;
      this.heroObserver = null;
      this.footerObserver = null;
      this.storyObserver = null;
      this.reachedSections = new WeakSet();
      this.imageCache = new Map();
      this.mediaRequest = 0;
      this.activeSceneIndex = 0;
      this.cartLocked = false;
      this.heroExited = false;
      this.storyConsumed = false;
      this.refreshTimer = null;
      this.mediaTimer = null;
      this.initialRevealTimer = null;

      const listenerOptions = { signal: this.abortController.signal };
      this.compactViewport.addEventListener('change', () => {
        this.syncCompactPreviews();
      }, listenerOptions);
      document.addEventListener('click', (event) => this.onClick(event), listenerOptions);
      document.addEventListener('submit', (event) => this.onSubmit(event), listenerOptions);
      document.addEventListener('input', (event) => this.onInput(event), listenerOptions);
      document.addEventListener('keydown', (event) => this.onKeydown(event), listenerOptions);
      document.addEventListener('toggle', (event) => this.onToggle(event), {
        capture: true,
        signal: this.abortController.signal
      });

      this.refresh();
      if (!namespace.pageViewEmitted) {
        namespace.pageViewEmitted = true;
        emit('landing_page_view');
      }
    }

    scheduleRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refresh(), 0);
    }

    disconnectObservers() {
      this.reachObserver?.disconnect();
      this.heroObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.storyObserver?.disconnect();
    }

    refresh() {
      this.disconnectObservers();
      if (!this.root?.isConnected) return;

      this.stage = this.root.querySelector('[data-lp-b-stage]');
      this.scenes = Array.from(this.root.querySelectorAll('[data-lp-b-scene]'));
      this.activeSceneIndex = Math.max(0, this.scenes.findIndex((scene) => scene.classList.contains('is-active')));
      this.sticky = this.root.querySelector('[data-lp-b-sticky]');
      this.reentries = Array.from(document.querySelectorAll('[data-lp-b-reentry]')).filter((entry) => {
        if (this.isStoryCommerce()) return false;
        const heroes = Array.from(document.querySelectorAll('[data-lp-b-commerce]:not([data-lp-b-layout="commerce"])'))
          .filter((hero) => !entry.dataset.productId || hero.dataset.productId === entry.dataset.productId);
        const preceding = heroes.filter((hero) => hero.compareDocumentPosition(entry) & Node.DOCUMENT_POSITION_FOLLOWING);
        return (preceding[preceding.length - 1] || heroes[0]) === this.root;
      });
      this.reentry = this.reentries[0] || null;
      this.initPurchase();
      this.initQuickColors();
      this.footer = document.querySelector('.shopify-section-group-footer-group, #shopify-section-footer, body > footer');

      const selected = this.selectedSwatch() || this.root.querySelector('[data-lp-b-swatch]:not(:disabled)');
      if (selected) {
        this.applyPalette(this.scenes[this.activeSceneIndex], selected);
        this.commitVariantState(selected, { updateMedia: false, emitSelection: false });
      }

      window.clearTimeout(this.initialRevealTimer);
      this.scenes.forEach((scene, index) => {
        scene.classList.remove('is-entering', 'is-exiting', 'is-initial-reveal');
        scene.classList.toggle('is-active', index === this.activeSceneIndex);
      });
      if (!motionPreference.matches) {
        const activeScene = this.scenes[this.activeSceneIndex];
        window.requestAnimationFrame(() => activeScene?.classList.add('is-initial-reveal'));
        this.initialRevealTimer = window.setTimeout(() => activeScene?.classList.remove('is-initial-reveal'), 980);
      }

      document.querySelectorAll('[data-lp-comparison]').forEach((comparison) => {
        const range = comparison.querySelector('[data-lp-comparison-range]');
        if (range) comparison.style.setProperty('--ln-pillow-compare-pos', `${range.value}%`);
      });

      this.initObservers();
      this.updateStickyVisibility();
      this.scheduleIdlePreload();
    }

    initObservers() {
      if (!('IntersectionObserver' in window)) {
        this.heroExited = this.root.getBoundingClientRect().bottom <= 0;
        this.updateStickyVisibility();
        return;
      }

      this.heroObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.heroExited = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.updateStickyVisibility();
      }, { threshold: 0 });
      const visibilityTarget = this.isStoryCommerce() ? document.querySelector('[data-lp-hero]') || this.root : this.root;
      this.heroObserver.observe(visibilityTarget);

      const storyGate = document.querySelector('[data-lp-section="controlled-comparison"]');
      if (storyGate) {
        this.storyObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.storyConsumed = true;
        }, { threshold: .12 });
        this.storyObserver.observe(storyGate);
      }

      if (this.footer && this.sticky) {
        this.footerObserver = new IntersectionObserver((entries) => {
          this.sticky?.classList.toggle('is-footer-visible', entries.some((entry) => entry.isIntersecting));
        }, { threshold: 0 });
        this.footerObserver.observe(this.footer);
      }

      const reachTargets = this.isPrimaryInstance() ? Array.from(document.querySelectorAll('[data-lp-reach-event]')) : [];
      if (reachTargets.length) {
        this.reachObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || this.reachedSections.has(entry.target)) return;
            this.reachedSections.add(entry.target);
            emit(entry.target.dataset.lpReachEvent, {
              section: entry.target.dataset.lpSection || ''
            });
            this.reachObserver?.unobserve(entry.target);
          });
        }, { threshold: .2, rootMargin: '0px 0px -12% 0px' });
        reachTargets.forEach((target) => this.reachObserver.observe(target));
      }
    }

    onClick(event) {
      const checkout = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
      if (checkout && this.isPrimaryInstance()) emit('begin_checkout', { source: 'cart_or_checkout_control' });

      const owner = event.target.closest?.('[data-lp-b-commerce], [data-lp-b-reentry]');
      if (owner && owner !== this.root && !this.reentries.includes(owner)) return;
      const quick = event.target.closest?.('[data-lp-b-quick-swatch]');
      if (quick && this.root.contains(quick) && !quick.disabled && !this.cartLocked) {
        const swatch = this.primarySwatches().find((item) => item.dataset.variantId === quick.dataset.lpBQuickSwatch);
        if (!this.available(swatch)) return;
        this.selectSwatch(swatch);
        return;
      }
      const modeButton = event.target.closest?.('[data-lp-b-purchase-mode]');
      if (modeButton && this.root.contains(modeButton)) {
        this.setPurchaseMode(modeButton.dataset.lpBPurchaseMode);
        return;
      }
      const secondary = event.target.closest?.('[data-lp-b-secondary-swatch]');
      if (secondary && this.root.contains(secondary) && !secondary.disabled && !this.cartLocked) {
        this.secondaryVariantId = secondary.dataset.variantId;
        this.syncPurchase();
        this.updateButtons('default');
        return;
      }
      const swatch = event.target.closest?.('[data-lp-b-swatch]');
      if (swatch && this.root.contains(swatch) && !swatch.disabled && !this.cartLocked) {
        this.selectSwatch(swatch);
        return;
      }

      const galleryMedia = event.target.closest?.('[data-lp-b-product-thumb], [data-lp-b-gallery-media]');
      if (galleryMedia && this.root.contains(galleryMedia)) {
        this.selectGalleryMedia(galleryMedia);
        return;
      }

      const jump = event.target.closest?.('[data-lp-jump-commerce]');
      if (jump && (owner === this.root || this.reentries.includes(owner) || (!owner && this.isPrimaryInstance()))) {
        event.preventDefault();
        const eventName = jump.dataset.lpEvent;
        if (eventName) emit(eventName, { cta: jump.dataset.lpCta || '' });
        this.scrollToHero(Boolean(jump.dataset.lpFocusSelector));
        return;
      }

      const stickyButton = event.target.closest?.('[data-lp-b-sticky-button]');
      if (stickyButton && this.root.contains(stickyButton)) {
        event.preventDefault();
        if (this.isStoryCommerce()) {
          emit('sticky_cta_click', this.currentVariantDetail());
          if (this.selectedSwatch()) this.addSelectedVariant(stickyButton, 'sticky');
          else this.scrollToHero(true);
        } else {
          emit('sticky_purchase_return', this.currentVariantDetail());
          this.scrollToHero(true);
        }
        return;
      }

      // Keep old markup compatible during a two-file theme update.
      const reentryButton = event.target.closest?.('[data-lp-b-reentry-link], [data-lp-b-reentry-atc]');
      if (reentryButton && this.reentries.some((entry) => entry.contains(reentryButton))) {
        event.preventDefault();
        emit('purchase_reentry_return', this.currentVariantDetail());
        this.scrollToHero(true);
      }
    }

    onSubmit(event) {
      const form = event.target.closest?.('[data-lp-b-product-form]');
      if (!form || !this.root.contains(form)) return;
      event.preventDefault();
      this.addSelectedVariant(event.submitter || form.querySelector('[data-lp-b-main-atc]'), form.dataset.atcSource || 'above_fold');
    }

    onInput(event) {
      if (!this.isPrimaryInstance()) return;
      const range = event.target.closest?.('[data-lp-comparison-range]');
      if (!range) return;
      range.closest('[data-lp-comparison]')?.style.setProperty('--ln-pillow-compare-pos', `${range.value}%`);
      this.markComparisonInteraction(range);
    }

    onKeydown(event) {
      const purchaseButton = event.target.closest?.('[data-lp-b-purchase-mode], [data-lp-b-swatch], [data-lp-b-secondary-swatch], [data-lp-b-quick-swatch]');
      if (purchaseButton && this.root.contains(purchaseButton) && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        if (!event.repeat && !purchaseButton.disabled) purchaseButton.click();
        return;
      }
      if (!this.isPrimaryInstance()) return;
      const range = event.target.closest?.('[data-lp-comparison-range]');
      if (!range || !['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'].includes(event.key)) return;
      event.preventDefault();

      const minimum = Number(range.min || 0);
      const maximum = Number(range.max || 100);
      const step = Number(range.step || 1);
      const current = Number(range.value || 0);
      let next = current;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current - step;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current + step;
      if (event.key === 'PageDown') next = current - (step * 10);
      if (event.key === 'PageUp') next = current + (step * 10);
      if (event.key === 'Home') next = minimum;
      if (event.key === 'End') next = maximum;

      range.value = String(Math.min(maximum, Math.max(minimum, next)));
      range.closest('[data-lp-comparison]')?.style.setProperty('--ln-pillow-compare-pos', `${range.value}%`);
      this.markComparisonInteraction(range);
    }

    onToggle(event) {
      if (!this.isPrimaryInstance()) return;
      const details = event.target.closest?.('[data-lp-faq-item]');
      if (!details) return;
      const summary = details.querySelector('summary');
      summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      if (details.open) {
        emit('faq_interaction', {
          question: details.dataset.lpFaqQuestion || summary?.textContent?.trim() || ''
        });
      }
    }

    markComparisonInteraction(range) {
      if (range.dataset.lpInteracted !== 'true' && Number(range.value) !== 50) {
        range.dataset.lpInteracted = 'true';
        emit('slider_first_interaction', { position: Number(range.value) });
      }
    }

    selectedSwatch() {
      return this.root?.querySelector('[data-lp-b-swatch][aria-pressed="true"]') || null;
    }

    setGalleryActive(control) {
      if (!this.root || !control) return;
      this.root.querySelectorAll('[data-lp-b-product-thumb], [data-lp-b-gallery-media]').forEach((button) => {
        const selected = button === control;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    updateProductThumbnail(swatch) {
      const productThumb = this.root?.querySelector('[data-lp-b-product-thumb]');
      if (!productThumb || !swatch?.dataset.mediaSrc) return;
      productThumb.dataset.mediaSrc = swatch.dataset.mediaSrc;
      productThumb.dataset.mediaSrcset = swatch.dataset.mediaSrcset || '';
      productThumb.dataset.mediaAlt = swatch.dataset.mediaAlt || swatch.dataset.color || '';
      const image = productThumb.querySelector('.ln-pillow-b-gallery__image');
      if (image) {
        image.src = swatch.dataset.mediaSrc;
        if (swatch.dataset.mediaSrcset) image.srcset = swatch.dataset.mediaSrcset;
        image.alt = productThumb.dataset.mediaAlt;
      }
      this.setGalleryActive(productThumb);
    }

    selectGalleryMedia(control) {
      if (!control || control.getAttribute('aria-pressed') === 'true') return;
      const controls = Array.from(this.root.querySelectorAll('[data-lp-b-product-thumb], [data-lp-b-gallery-media]'));
      const previous = this.root.querySelector('[data-lp-b-product-thumb][aria-pressed="true"], [data-lp-b-gallery-media][aria-pressed="true"]');
      const direction = controls.indexOf(control) >= controls.indexOf(previous) ? 'forward' : 'backward';
      this.setGalleryActive(control);
      this.transitionMedia(control, direction, this.mediaSwatch());
      emit('gallery_media_selected', {
        ...this.currentVariantDetail(),
        mediaSource: control.dataset.mediaSource || 'model_gallery',
        mediaAlt: control.dataset.mediaAlt || ''
      });
    }

    currentVariantDetail() {
      const swatch = this.selectedSwatch();
      return {
        productId: swatch?.dataset.productId || '',
        variantId: swatch?.dataset.variantId || '',
        color: swatch?.dataset.color || '',
        price: Number(swatch?.dataset.priceCents || 0),
        atc_context: this.storyConsumed ? 'story_consumed' : 'above_fold'
      };
    }

    selectSwatch(swatch) {
      const swatches = Array.from(this.root.querySelectorAll('[data-lp-b-swatch]'));
      const previous = this.selectedSwatch();
      const previousIndex = Math.max(0, swatches.indexOf(previous));
      const nextIndex = Math.max(0, swatches.indexOf(swatch));
      const direction = nextIndex >= previousIndex ? 'forward' : 'backward';

      swatches.forEach((button) => {
        const selected = button === swatch;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      this.commitVariantState(swatch, { updateMedia: true, emitSelection: true, direction });
    }

    commitVariantState(swatch, { updateMedia, emitSelection, direction = 'forward' }) {
      const available = swatch.dataset.available === 'true' && !swatch.disabled;
      const color = swatch.dataset.color || '';
      const price = swatch.dataset.priceLabel || this.root.dataset.defaultPrice || '';
      const variantId = swatch.dataset.variantId || '';

      this.root.querySelectorAll('[data-lp-b-selected-color], [data-lp-b-media-color]').forEach((label) => {
        label.textContent = color;
      });
      this.purchaseTargets('[data-lp-b-reentry-color]').forEach((label) => {
        label.textContent = color;
      });
      this.root.querySelectorAll('[data-lp-b-price]').forEach((label) => {
        label.textContent = price;
      });
      this.purchaseTargets('[data-lp-b-reentry-price], [data-lp-b-sticky-price]').forEach((label) => {
        label.textContent = price;
      });
      this.root.querySelectorAll('[data-lp-b-variant-input]').forEach((input) => {
        input.value = variantId;
      });
      this.root.querySelectorAll('[data-lp-b-availability]').forEach((status) => {
        status.textContent = available ? 'Available' : 'Sold out';
      });

      this.purchaseTargets('.ln-pillow-b-reentry__image').forEach((reentryImage) => {
        if (!swatch.dataset.mediaSrc) return;
        reentryImage.src = swatch.dataset.mediaSrc;
        if (swatch.dataset.mediaSrcset) reentryImage.srcset = swatch.dataset.mediaSrcset;
        else reentryImage.removeAttribute('srcset');
        reentryImage.alt = swatch.dataset.mediaAlt || reentryImage.alt;
      });

      this.updateProductThumbnail(swatch);
      // Story-first Commerce keeps its original, non-animated product image.
      if (this.isStoryCommerce()) {
        const image = this.root.querySelector('.ln-pillow-commerce__image');
        if (image && swatch.dataset.mediaSrc) {
          image.src = swatch.dataset.mediaSrc;
          if (swatch.dataset.mediaSrcset) image.srcset = swatch.dataset.mediaSrcset;
          else image.removeAttribute('srcset');
          image.alt = swatch.dataset.mediaAlt || color;
        }
      }

      if (variantId && window.history?.replaceState && (emitSelection || this.isPrimaryInstance())) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({ variantId }, '', url);
      }

      if (!updateMedia) this.applyHeroPalette(swatch);
      this.syncPurchase();
      this.updateButtons('default');
      if (updateMedia) this.transitionMedia(swatch, direction);
      if (emitSelection) {
        emit('color_selected', {
          color,
          productId: swatch.dataset.productId || '',
          variantId,
          price: Number(swatch.dataset.priceCents || 0)
        });
      }
    }

    applyHeroPalette(swatch) {
      if (!this.root || !swatch || this.isStoryCommerce()) return;
      const key = (swatch.dataset.color || '').trim().toLowerCase();
      const palette = SECTION_PALETTES[key] || SECTION_PALETTES['onyx black'];
      this.root.style.setProperty('--lpb-section-bg-1', palette[0]);
      this.root.style.setProperty('--lpb-section-bg-2', palette[1]);
      this.root.style.setProperty('--lpb-section-glow', palette[2]);
      this.root.dataset.lpBSceneColor = swatch.dataset.color || '';
    }

    applyPalette(scene, swatch) {
      if (!scene || !swatch) return;
      scene.style.setProperty('--lpb-bg1', swatch.dataset.sceneBg1 || '#e9e1d8');
      scene.style.setProperty('--lpb-bg2', swatch.dataset.sceneBg2 || '#c6b8ac');
      scene.style.setProperty('--lpb-blob-a', swatch.dataset.sceneBlobA || 'rgba(110,98,88,.26)');
      scene.style.setProperty('--lpb-blob-b', swatch.dataset.sceneBlobB || 'rgba(246,241,233,.92)');
      scene.style.setProperty('--lpb-shadow', swatch.dataset.sceneShadow || 'rgba(53,46,41,.16)');
      scene.style.setProperty('--lpb-x', swatch.dataset.sceneX || '0%');
      scene.style.setProperty('--lpb-y', swatch.dataset.sceneY || '0%');
      scene.style.setProperty('--lpb-scale', swatch.dataset.sceneScale || '1');
      scene.style.setProperty('--lpb-rotation', swatch.dataset.sceneRotation || '-15deg');
    }

    buildScene(scene, swatch, image, mediaControl = swatch) {
      scene.innerHTML = '<div class="ln-pillow-b-scene__background"></div><div class="ln-pillow-b-scene__accent ln-pillow-b-scene__accent--a"></div><div class="ln-pillow-b-scene__accent ln-pillow-b-scene__accent--b"></div><div class="ln-pillow-b-scene__shadow"></div><div class="ln-pillow-b-scene__product"></div>';
      image.className = 'ln-pillow-b-scene__image';
      image.sizes = '(min-width: 750px) 36vw, 58vw';
      const product = scene.querySelector('.ln-pillow-b-scene__product');
      if (mediaControl?.dataset.mediaKind === 'model') product?.classList.add('ln-pillow-b-scene__product--model');
      product?.append(image);
      this.applyPalette(scene, swatch);
      if (mediaControl?.dataset.mediaKind === 'model') {
        scene.style.setProperty('--lpb-rotation', '0deg');
        scene.style.setProperty('--lpb-scale', '1');
      }
    }

    loadVariantImage(swatch, priority = 'high') {
      const src = swatch.dataset.mediaSrc;
      if (!src) return Promise.reject(new Error('Variant media is missing.'));
      if (this.imageCache.has(src)) return this.imageCache.get(src).then((image) => image.cloneNode());

      const promise = new Promise((resolve, reject) => {
        const image = new Image();
        image.alt = swatch.dataset.mediaAlt || swatch.dataset.color || '';
        image.decoding = 'async';
        image.fetchPriority = priority;
        if (swatch.dataset.mediaSrcset) image.srcset = swatch.dataset.mediaSrcset;
        image.sizes = '(min-width: 750px) 36vw, 58vw';
        image.onload = async () => {
          try {
            await image.decode?.();
          } catch (_) {
            // A completed load is still safe to display when decode() is unavailable.
          }
          resolve(image);
        };
        image.onerror = () => reject(new Error(`Unable to load media for ${swatch.dataset.color || 'the selected variant'}.`));
        image.src = src;
      });
      this.imageCache.set(src, promise);
      return promise.then((image) => image.cloneNode());
    }

    async transitionMedia(mediaControl, direction = 'forward', paletteSwatch = mediaControl) {
      if (!this.stage || this.scenes.length < 2) return;
      const requestId = ++this.mediaRequest;
      window.clearTimeout(this.mediaTimer);
      this.stage.classList.remove('is-transitioning', 'is-forward', 'is-backward');
      this.scenes.forEach((scene, index) => {
        scene.classList.remove('is-entering', 'is-exiting', 'is-initial-reveal');
        scene.classList.toggle('is-active', index === this.activeSceneIndex);
      });

      try {
        const image = await this.loadVariantImage(mediaControl, 'high');
        const isVariantMedia = mediaControl.matches?.('[data-lp-b-swatch]');
        const isCurrentMedia = isVariantMedia
          ? mediaControl === this.mediaSwatch()
          : mediaControl.getAttribute('aria-pressed') === 'true';
        if (requestId !== this.mediaRequest || !isCurrentMedia) return;

        const current = this.scenes[this.activeSceneIndex];
        const nextIndex = this.activeSceneIndex === 0 ? 1 : 0;
        const next = this.scenes[nextIndex];
        this.applyHeroPalette(paletteSwatch || this.selectedSwatch());
        this.buildScene(next, paletteSwatch || this.selectedSwatch(), image, mediaControl);

        if (motionPreference.matches) {
          current.className = 'ln-pillow-b-scene';
          next.className = 'ln-pillow-b-scene is-active';
          this.activeSceneIndex = nextIndex;
          this.stage.classList.remove('is-transitioning', 'is-forward', 'is-backward');
          emit('variant_media_transition', {
            ...this.currentVariantDetail(),
            motion: 'reduced',
            mediaSource: mediaControl.dataset.mediaSource || 'variant_featured_media'
          });
          return;
        }

        this.stage.classList.add('is-transitioning', direction === 'backward' ? 'is-backward' : 'is-forward');
        next.className = 'ln-pillow-b-scene is-entering';
        current.className = 'ln-pillow-b-scene is-exiting';
        this.mediaTimer = window.setTimeout(() => {
          if (requestId !== this.mediaRequest) return;
          current.className = 'ln-pillow-b-scene';
          next.className = 'ln-pillow-b-scene is-active';
          this.activeSceneIndex = nextIndex;
          this.stage.classList.remove('is-transitioning', 'is-forward', 'is-backward');
          emit('variant_media_transition', {
            ...this.currentVariantDetail(),
            motion: 'animated',
            durationMs: 760,
            mediaSource: mediaControl.dataset.mediaSource || 'variant_featured_media'
          });
        }, 760);
      } catch (error) {
        if (requestId === this.mediaRequest) this.stage.classList.remove('is-transitioning', 'is-forward', 'is-backward');
        emit('variant_media_failure', {
          ...this.currentVariantDetail(),
          message: error.message || 'Product media failed to load.'
        });
      }
    }

    scheduleIdlePreload() {
      if (this.isStoryCommerce()) return;
      const preload = () => {
        const selected = this.selectedSwatch();
        const queue = Array.from(this.root.querySelectorAll('[data-lp-b-swatch]:not(:disabled)'))
          .filter((swatch) => swatch !== selected);
        const loadNext = () => {
          const swatch = queue.shift();
          if (!swatch) return;
          this.loadVariantImage(swatch, 'low').catch(() => {}).finally(() => {
            window.setTimeout(loadNext, 90);
          });
        };
        loadNext();
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preload, { timeout: 2600 });
      } else {
        window.setTimeout(preload, 1400);
      }
    }

    labels() {
      return {
        choose: this.root.dataset.chooseLabel || 'Choose Your Color',
        add: this.root.dataset.addLabel || 'ADD TO BAG',
        chooseSecond: this.root.dataset.chooseSecondLabel || 'CHOOSE SECOND COLOR',
        soldOut: this.root.dataset.soldOutLabel || 'Sold Out',
        adding: 'ADDING…',
        added: this.root.dataset.addedLabel || 'Added',
        error: this.root.dataset.errorLabel || 'Couldn’t add — try again'
      };
    }


    isStoryCommerce() {
      return this.root.dataset.lpBLayout === 'commerce';
    }

    isPrimaryInstance() {
      // The Story controller owns its page-level links, comparison and FAQ events.
      return !this.isStoryCommerce() && document.querySelector('[data-lp-b-commerce]:not([data-lp-b-layout="commerce"])') === this.root;
    }

    purchaseTargets(selector) {
      return [this.root, ...(this.reentries || [])].flatMap((element) => Array.from(element.querySelectorAll(selector)));
    }

    primarySwatches() {
      return Array.from(this.root.querySelectorAll('[data-lp-b-swatch]'));
    }

    secondarySwatch() {
      if (!this.secondaryVariantId) return null;
      return this.primarySwatches().find((swatch) => swatch.dataset.variantId === this.secondaryVariantId) || null;
    }

    available(swatch) {
      return Boolean(swatch && !swatch.disabled && swatch.dataset.available === 'true');
    }

    duoAvailable() {
      return this.root.dataset.duoEnabled === 'true' && Boolean(this.root.querySelector('[data-lp-b-purchase-mode="duo"]'));
    }

    duoVerified() {
      return this.root.dataset.duoConfirmed === 'true';
    }

    initPurchase() {
      if (this.purchaseInitialized) return;
      this.purchaseInitialized = true;
      const secondaryGroup = this.root.querySelector('[data-lp-b-secondary-swatches]');
      this.primarySwatches().forEach((primary) => {
        if (!secondaryGroup) return;
        const secondary = primary.cloneNode(true);
        secondary.removeAttribute('id');
        secondary.removeAttribute('data-lp-b-swatch');
        secondary.setAttribute('data-lp-b-secondary-swatch', '');
        secondary.setAttribute('aria-pressed', 'false');
        secondary.classList.remove('is-active');
        // Reuse primary variant data; Color 2 has no separate media engine.
        Array.from(secondary.attributes).filter((attr) => /^(data-media-|data-scene-)/.test(attr.name))
          .forEach((attr) => secondary.removeAttribute(attr.name));
        secondaryGroup.append(secondary);
      });
      this.root.querySelectorAll('[data-lp-b-purchase-mode]').forEach((button) => { button.disabled = false; });
      this.mode = this.root.dataset.defaultPurchaseMode === 'duo' && this.duoAvailable() ? 'duo' : 'single';
      this.syncPurchase();
    }

    setPurchaseMode(mode) {
      if (this.cartLocked || !['single', 'duo'].includes(mode) || (mode === 'duo' && !this.duoAvailable())) return;
      this.mode = mode;
      if (this.secondaryVariantId && !this.available(this.secondarySwatch())) this.secondaryVariantId = '';
      this.syncPurchase();
      this.updateButtons('default');
    }

    duoPercent() {
      // A separate setting avoids reusing the saved 15%-off-both-items value.
      const percent = Number(this.root.dataset.duoSecondItemPercent ?? 30);
      return Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 30;
    }

    percentageDiscount(price) {
      // Shopify rounds the discounted unit down. Zero-decimal currencies
      // still use amount * 100 in Liquid/Ajax; their discount quantum is 100.
      let digits = 2;
      try {
        digits = new Intl.NumberFormat('en', { style: 'currency', currency: this.root.dataset.currency }).resolvedOptions().maximumFractionDigits;
      } catch (_) { /* Keep the normal cent quantum for an unknown currency. */ }
      const quantum = digits === 0 ? 100 : 1;
      return Math.floor((price * this.duoPercent()) / (100 * quantum)) * quantum;
    }

    priceState() {
      const primaryPrice = Number(this.selectedSwatch()?.dataset.priceCents || 0);
      const secondaryPrice = Number(this.secondarySwatch()?.dataset.priceCents ?? primaryPrice);
      const regular = primaryPrice + secondaryPrice;
      // Native Buy X Get Y discounts the lower-priced eligible unit, not Color 2
      // specifically. Apply the percentage once per pair, never to both items.
      const discount = this.percentageDiscount(Math.min(primaryPrice, secondaryPrice));
      return { single: primaryPrice, regular, discount, duo: regular - discount };
    }

    mediaSwatch() {
      return this.selectedSwatch();
    }

    initQuickColors() {
      const rail = this.root.querySelector('[data-lp-b-quick]');
      const group = rail?.querySelector('[data-lp-b-quick-colors]');
      if (!group) return;
      // This is a view onto the existing variants, not a second product selector.
      // Rebuild only on initial setup so refreshes do not steal keyboard focus.
      if (!group.children.length) {
        this.primarySwatches().forEach((swatch) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ln-pillow-b-quick__swatch';
          button.dataset.lpBQuickSwatch = swatch.dataset.variantId;
          button.style.setProperty('--ln-pillow-b-swatch', swatch.style.getPropertyValue('--ln-pillow-b-swatch'));
          button.setAttribute('aria-label', 'Select ' + swatch.dataset.color);
          button.title = swatch.dataset.color;
          group.append(button);
        });
      }
      rail.hidden = false;
      this.syncQuickColors();
    }

    syncQuickColors() {
      const rail = this.root.querySelector('[data-lp-b-quick]');
      if (!rail) return;
      const selected = this.selectedSwatch();
      rail.querySelectorAll('[data-lp-b-quick-swatch]').forEach((button) => {
        const swatch = this.primarySwatches().find((item) => item.dataset.variantId === button.dataset.lpBQuickSwatch);
        button.setAttribute('aria-pressed', String(swatch === selected));
        button.disabled = this.cartLocked || !this.available(swatch);
      });
      const name = rail.querySelector('[data-lp-b-quick-name]');
      if (name) name.textContent = selected?.dataset.color || '';
    }

    money(cents, currency = this.root.dataset.currency || window.Shopify?.currency?.active || 'USD') {
      if (currency === this.root.dataset.shopCurrency && typeof window.Shopify?.formatMoney === 'function') {
        const container = document.createElement('span');
        container.innerHTML = window.Shopify.formatMoney(cents, this.root.dataset.moneyFormat);
        return container.textContent;
      }
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0
        }).format(cents / 100);
      } catch (_) {
        return `${currency} ${(cents / 100).toFixed(2)}`;
      }
    }

    syncPurchase() {
      const primaryGroup = this.root.querySelector('[data-lp-b-primary-colors]');
      const slot = this.root.querySelector(`[data-lp-b-primary-slot="${this.mode}"]`);
      if (primaryGroup && slot && primaryGroup.parentElement !== slot) slot.append(primaryGroup);
      this.root.querySelectorAll('[data-lp-b-purchase-mode]').forEach((button) => {
        const selected = button.dataset.lpBPurchaseMode === this.mode;
        button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-expanded', String(selected));
      });
      this.root.querySelectorAll('[data-lp-b-purchase-card]').forEach((card) => card.classList.toggle('is-selected', card.dataset.lpBPurchaseCard === this.mode));
      this.root.querySelectorAll('[data-lp-b-purchase-panel]').forEach((panel) => { panel.hidden = panel.dataset.lpBPurchasePanel !== this.mode; });
      const colorLabel = this.mode === 'duo' ? this.root.dataset.firstColorLabel || 'COLOR 1' : this.root.dataset.singleColorLabel || 'COLOR';
      this.root.querySelectorAll('[data-lp-b-primary-color-label]').forEach((label) => { label.textContent = colorLabel; });
      primaryGroup?.querySelector('[role="group"]')?.setAttribute('aria-label', colorLabel);
      const secondary = this.secondarySwatch();
      this.root.querySelectorAll('[data-lp-b-secondary-swatch]').forEach((swatch) => {
        const selected = Boolean(this.secondaryVariantId) && swatch.dataset.variantId === this.secondaryVariantId;
        swatch.classList.toggle('is-active', selected);
        swatch.setAttribute('aria-pressed', String(selected));
      });
      this.root.querySelectorAll('[data-lp-b-secondary-color]').forEach((label) => {
        label.textContent = secondary?.dataset.color || this.root.dataset.secondColorPrompt || 'Choose your second color';
      });
      const prices = this.priceState();
      [['single-price', prices.single], ['duo-price', prices.duo], ['duo-regular', prices.regular]].forEach(([key, value]) => {
        this.root.querySelectorAll(`[data-lp-b-${key}]`).forEach((label) => { label.textContent = value === null ? '—' : this.money(value); });
      });
      this.root.querySelectorAll('[data-lp-b-duo-regular]').forEach((label) => { label.hidden = !prices.discount; });
      this.root.querySelectorAll('[data-lp-b-duo-saving]').forEach((label) => {
        label.textContent = `2nd item −${this.money(prices.discount)} (${this.duoPercent()}% off)`;
      });
      this.purchaseTargets('[data-lp-b-reentry-price], [data-lp-b-sticky-price]').forEach((label) => {
        label.textContent = prices[this.mode] === null ? 'Discount in cart' : this.money(prices[this.mode]);
      });
      this.purchaseTargets('[data-lp-b-reentry-color]').forEach((label) => {
        label.textContent = this.mode === 'duo'
          ? `Duo · ${this.selectedSwatch()?.dataset.color || ''} + ${secondary?.dataset.color || 'Choose second color'}`
          : this.selectedSwatch()?.dataset.color || '';
      });
      this.syncCompactPreviews();
      this.syncQuickColors();
    }


    syncCompactPreviews() {
      // No second variant system: both previews read the existing selected swatches.
      if (!this.compactViewport.matches) {
        this.compactPreviews.forEach((state) => { state.token += 1; state.key = ''; });
        return;
      }
      this.root.querySelectorAll('[data-lp-b-compact-preview]').forEach((row) => {
        if (row.closest('[data-lp-b-purchase-panel]')?.hidden) return;
        const swatch = row.dataset.lpBCompactPreview === 'secondary' ? this.secondarySwatch() : this.selectedSwatch();
        this.updateCompactPreview(row, swatch);
      });
    }

    loadCompactImage(src) {
      if (!this.compactImages.has(src)) {
        const pending = new Promise((resolve, reject) => {
          const image = new Image(80, 80);
          image.decoding = 'async';
          image.onload = async () => {
            if (!image.naturalWidth) { reject(new Error('Preview image unavailable')); return; }
            try { await image.decode?.(); } catch (_) { /* A loaded image remains usable. */ }
            resolve(image);
          };
          image.onerror = () => reject(new Error('Preview image unavailable'));
          image.src = src;
        });
        this.compactImages.set(src, pending);
      }
      return this.compactImages.get(src).then((image) => image.cloneNode());
    }

    async updateCompactPreview(row, swatch) {
      const frame = row.querySelector('[data-lp-b-preview-frame]');
      const copy = row.querySelector('[data-lp-b-preview-copy]');
      const placeholder = row.querySelector('[data-lp-b-preview-placeholder]');
      if (!frame || !copy || !placeholder) return;
      let state = this.compactPreviews.get(row);
      if (!state) { state = { token: 0, key: '' }; this.compactPreviews.set(row, state); }
      const sources = swatch ? [...new Set([
        swatch.dataset.previewSrc,
        swatch.dataset.previewMappedSrc,
        this.root.dataset.previewProductSrc
      ].filter(Boolean))] : [];
      const key = JSON.stringify([swatch?.dataset.variantId || '', ...sources]);
      if (state.key === key) return;
      state.key = key;
      const token = ++state.token;
      const current = () => state.token === token && this.root.isConnected && this.compactViewport.matches;
      if (!swatch) {
        frame.querySelectorAll('img').forEach((image) => image.remove());
        placeholder.textContent = row.dataset.lpBCompactPreview === 'secondary' ? '+' : '';
        copy.textContent = row.dataset.lpBCompactPreview === 'secondary' ? 'Choose your second color' : 'Choose a color';
        row.setAttribute('aria-busy', 'false');
        return;
      }
      row.setAttribute('aria-busy', 'true');
      copy.textContent = 'Loading preview…';
      // Keep the last decoded image visible while loading; stale requests cannot win.
      for (const src of sources) {
        if (!current()) return;
        try {
          const image = await this.loadCompactImage(src);
          if (!current()) return;
          image.className = 'ln-pillow-b-compact__image';
          image.alt = [this.root.getAttribute('aria-label'), swatch.dataset.color].filter(Boolean).join(' — ');
          const previous = Array.from(frame.querySelectorAll('img'));
          frame.append(image);
          placeholder.textContent = '';
          copy.textContent = row.dataset.lpBCompactPreview === 'secondary' ? 'Selected color' : 'Live product preview';
          row.setAttribute('aria-busy', 'false');
          window.requestAnimationFrame(() => {
            if (!current()) { image.remove(); return; }
            image.classList.add('is-visible');
            previous.forEach((old) => { old.classList.remove('is-visible'); old.setAttribute('aria-hidden', 'true'); });
            window.setTimeout(() => previous.forEach((old) => old.remove()), motionPreference.matches ? 0 : 160);
          });
          return;
        } catch (_) {
          // Try the existing per-color mapping, then the product's featured image.
        }
      }
      if (current()) {
        frame.querySelectorAll('img').forEach((image) => image.remove());
        placeholder.textContent = '';
        copy.textContent = 'Preview unavailable';
        row.setAttribute('aria-busy', 'false');
      }
    }

    purchaseItems() {
      const first = this.selectedSwatch();
      if (!this.available(first)) return [];
      const firstId = Number(first.dataset.variantId);
      if (!Number.isSafeInteger(firstId) || firstId <= 0) return [];
      if (this.mode === 'single') return [{ id: firstId, quantity: 1 }];
      const second = this.secondarySwatch();
      if (!this.duoAvailable() || !this.duoVerified() || !this.available(second)) return [];
      const secondId = Number(second.dataset.variantId);
      if (!Number.isSafeInteger(secondId) || secondId <= 0) return [];
      return firstId === secondId ? [{ id: firstId, quantity: 2 }] : [{ id: firstId, quantity: 1 }, { id: secondId, quantity: 1 }];
    }

    updateButtons(state = 'default', message = '') {
      const labels = this.labels();
      const swatch = this.selectedSwatch();
      const price = this.priceState()[this.mode];
      let label = swatch ? (price === null ? `${labels.add} — DUO` : `${labels.add} — ${this.money(price)}`) : labels.choose;
      let disabled = !this.available(swatch);
      if (swatch && disabled) label = labels.soldOut;
      if (this.mode === 'duo') {
        if (!this.secondarySwatch()) {
          label = labels.chooseSecond;
          disabled = true;
        } else if (!this.available(this.secondarySwatch())) {
          label = labels.soldOut;
          disabled = true;
        } else if (!this.duoVerified()) {
          label = 'DUO SETUP REQUIRED';
          disabled = true;
        }
      }
      if (state === 'loading' || this.cartLocked) {
        label = labels.adding;
        disabled = true;
      }
      this.purchaseTargets('[data-lp-b-main-atc]').forEach((button) => {
        button.disabled = disabled;
        button.textContent = label;
        button.setAttribute('aria-busy', String(this.cartLocked));
      });
      const stickyLabel = this.root.dataset.stickyAddLabel || (this.isStoryCommerce() ? labels.add : 'ADD TO CART');
      this.root.querySelectorAll('[data-lp-b-sticky-button]').forEach((button) => {
        if (this.isStoryCommerce()) {
          button.disabled = disabled;
          button.textContent = disabled ? label : (price === null ? `${stickyLabel} — DUO` : `${stickyLabel} — ${this.money(price)}`);
          button.setAttribute('aria-busy', String(this.cartLocked));
        } else {
          // B Hero sticky is a navigation shortcut, not a second cart submitter.
          button.disabled = false;
          button.textContent = price === null ? `${stickyLabel} — DUO` : `${stickyLabel} — ${this.money(price)}`;
          button.setAttribute('aria-busy', 'false');
        }
      });
      this.root.querySelectorAll('[data-lp-b-purchase-mode]').forEach((button) => { button.disabled = this.cartLocked; });
      this.syncQuickColors();
      this.purchaseTargets('[data-lp-b-cart-status], [data-lp-b-reentry-status]').forEach((status) => {
        status.textContent = message;
      });
    }

    async readCart() {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Could not verify your bag. Please try again.');
      return response.json();
    }

    eligibleCartState(cart) {
      const ids = new Set(this.primarySwatches().map((swatch) => swatch.dataset.variantId));
      return (cart.items || []).filter((item) => ids.has(String(item.variant_id || item.id)))
        .reduce((sum, item) => ({
          quantity: sum.quantity + item.quantity,
          total: sum.total + item.final_line_price
        }), { quantity: 0, total: 0 });
    }

    duoCartFeedback(cart) {
      if (cart.currency && cart.currency !== this.root.dataset.currency) {
        return 'Items were added. Your cart currency changed; open your bag to confirm the current discount and total.';
      }
      const ids = new Set(this.primarySwatches().map((swatch) => swatch.dataset.variantId));
      const lines = (cart.items || []).filter((item) => ids.has(String(item.variant_id || item.id)));
      if (!lines.length || lines.some((line) => !Number.isFinite(line.original_line_price) || !Number.isFinite(line.final_line_price) || !(line.quantity > 0))) {
        return 'Items were added. Open your bag to confirm the Shopify-calculated discount and total.';
      }
      // Keep normal add-success feedback. Shopify owns BXGY line allocation,
      // including repeated pairs, odd quantities and combined discounts.
      return '';
    }

    async renderCart(data, trigger) {
      const cartUI = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      let rendered = false;
      if (typeof cartUI?.renderContents === 'function' && data.sections && Object.values(data.sections).every((html) => typeof html === 'string')) {
        try {
          cartUI.classList.remove('is-empty');
          cartUI.setActiveElement?.(trigger);
          // Ajax multi-add returns items[]. Dawn notification needs the first item's key.
          cartUI.renderContents({ ...data, ...(data.items?.[0] || {}), sections: data.sections });
          rendered = true;
        } catch (_) {
          // A rendering failure is not an add failure: never repeat the POST.
        }
      }
      if (!rendered) {
        await this.updateCartCount().catch(() => {});
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pillow-purchase', productVariantId: this.selectedSwatch()?.dataset.variantId, cartData: data });
      }
      return rendered;
    }

    updateStickyVisibility() {
      this.sticky?.classList.toggle('is-visible', this.heroExited);
    }

    scrollToHero(focusSelector = false) {
      this.root?.scrollIntoView({ behavior: motionBehavior(), block: 'start' });
      if (focusSelector) {
        window.setTimeout(() => {
          this.root?.querySelector('[data-lp-b-swatch]:not(:disabled)')?.focus({ preventScroll: true });
        }, motionBehavior() === 'smooth' ? 450 : 0);
      }
    }

    async updateCartCount() {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        bubble.classList.toggle('hidden', cart.item_count === 0);
        const visible = bubble.querySelector('[aria-hidden="true"]');
        const hidden = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = cart.item_count;
        if (hidden) hidden.textContent = `${cart.item_count} items`;
      });
    }


    async addSelectedVariant(trigger, source) {
      if (this.cartLocked || namespace.cartLocked) return;
      const items = this.purchaseItems();
      if (!items.length) return;
      this.cartLocked = true;
      namespace.cartLocked = true;
      this.updateButtons('loading');
      const isDuo = this.mode === 'duo';
      const trackingDetail = { ...this.currentVariantDetail(), atcSource: source, purchaseMode: this.mode, items };
      if (source === 'above_fold') emit('above_the_fold_atc', trackingDetail);
      let added = false;
      let submitted = false;
      let rejected = false;
      let feedback = '';
      try {
        if (isDuo) {
          const beforeCart = await this.readCart();
          if (beforeCart.currency && beforeCart.currency !== this.root.dataset.currency) {
            throw new Error('Your cart currency changed. Refresh this page before adding your Duo.');
          }
        }
        const cartUI = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = [...new Set((cartUI?.getSectionsToRender?.() || []).map((section) => section.id))].slice(0, 5);
        const rootUrl = window.Shopify?.routes?.root || '/';
        // Locale-aware /cart/add.js: exactly one POST for either purchase mode.
        submitted = true;
        const response = await fetch(window.routes?.cart_add_url || `${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}) })
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          rejected = true;
          // Shopify can partially fulfil an inventory-limited quantity; refresh without retrying.
          await this.refreshCartAfterError(cartUI, sections, trigger);
          throw new Error(data.description || data.message || this.labels().error);
        }
        added = true;
        const rendered = await this.renderCart(data, trigger);
        feedback = rendered ? 'Added to your bag.' : 'Added to your bag. Please open your bag to review it.';
        emit('add_to_cart', trackingDetail);
        if (isDuo) {
          const afterCart = await this.readCart();
          feedback = this.duoCartFeedback(afterCart) || feedback;
        }
      } catch (error) {
        feedback = added
          ? 'Items were added. Please open your bag to confirm the discount and total before checkout.'
          : submitted && !rejected
            ? 'Could not confirm the add request. Please check your bag before trying again to avoid adding duplicates.'
            : error.message || this.labels().error;
      } finally {
        this.cartLocked = false;
        namespace.cartLocked = false;
        this.updateButtons(added ? 'default' : 'error', feedback);
      }
    }

    async refreshCartAfterError(cartUI, sections, trigger) {
      try {
        if (cartUI && sections.length) {
          const url = new URL(window.location.href);
          url.search = '';
          url.searchParams.set('sections', sections.join(','));
          const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (response.ok) {
            const html = await response.json();
            if (cartUI.tagName.toLowerCase() === 'cart-drawer') await this.renderCart({ sections: html }, trigger);
          }
        }
        await this.updateCartCount();
      } catch (_) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }

    destroy() {
      this.compactPreviews.forEach((state) => { state.token += 1; });
      this.compactImages.clear();
      window.clearTimeout(this.refreshTimer);
      window.clearTimeout(this.mediaTimer);
      window.clearTimeout(this.initialRevealTimer);
      this.disconnectObservers();
      this.abortController.abort();
    }
  }

  namespace.controllers ||= new Map();
  const boot = () => {
    namespace.controllers.forEach((controller, root) => {
      if (!root.isConnected) { controller.destroy(); namespace.controllers.delete(root); }
    });
    document.querySelectorAll('[data-lp-b-commerce]').forEach((root) => {
      if (!namespace.controllers.has(root)) namespace.controllers.set(root, new PillowCommerceBController(root));
    });
    namespace.controllers.forEach((controller) => controller.refresh());
    namespace.controller = namespace.controllers.values().next().value;
    namespace.emit = emit;
    namespace.trackPurchase = (detail = {}) => emit('purchase', detail);
  };
  if (!namespace.lifecycleBound) {
    namespace.lifecycleBound = true;
    document.addEventListener('shopify:section:load', () => boot());
    document.addEventListener('shopify:section:unload', () => window.setTimeout(boot, 0));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
